import type { ConversationPersistence } from '@/services/types'
import {
  getConfiguredBot,
  getConfiguredBots,
  getDefaultBot,
  getDefaultConversationMode,
  getRuntimeContext,
  type ConfiguredBot,
  type RuntimeContext,
} from '@/config'
import { authSession } from '@/auth/session'

export interface AuthState {
  isAuthenticated: boolean
}

export interface ResolvedChatContext {
  authState: AuthState
  runtimeContext: RuntimeContext
  bot?: ConfiguredBot
  access: 'allowed' | 'requires_auth'
  conversationMode: ConversationPersistence
  historyEnabled: boolean
  botSelectionEnabled: boolean
}

export interface ResolveChatContextOptions {
  botId?: string | null
  authState?: AuthState
}

function resolvePreferredConversationMode(
  bot: ConfiguredBot | undefined,
  runtimeContext: RuntimeContext,
  authState: AuthState,
): ConversationPersistence {
  if (runtimeContext.isEmbedded) {
    return bot?.embeddedConversationMode ?? getDefaultConversationMode(runtimeContext, authState)
  }
  return bot?.defaultConversationMode ?? getDefaultConversationMode(runtimeContext, authState)
}

export function resolveChatContext(options: ResolveChatContextOptions = {}): ResolvedChatContext {
  const authState = options.authState ?? authSession
  const runtimeContext = getRuntimeContext()
  const configuredBots = getConfiguredBots()
  const bot = options.botId ? getConfiguredBot(options.botId) : getDefaultBot()
  const access = bot?.requiresAuth && !authState.isAuthenticated ? 'requires_auth' : 'allowed'

  let conversationMode = resolvePreferredConversationMode(bot, runtimeContext, authState)

  if (!authState.isAuthenticated && !(bot?.allowAnonymousHistory ?? false)) {
    conversationMode = 'ephemeral'
  }

  if (runtimeContext.isEmbedded && !(bot?.allowEmbeddedHistory ?? false) && conversationMode === 'persistent') {
    conversationMode = 'ephemeral'
  }

  const historyEnabled = access === 'allowed' && conversationMode === 'persistent'
  const botSelectionEnabled = !bot?.isStatic && configuredBots.length !== 1

  return {
    authState,
    runtimeContext,
    bot,
    access,
    conversationMode,
    historyEnabled,
    botSelectionEnabled,
  }
}
